import { useState, useEffect } from 'react';

import { useMutation } from '@tanstack/react-query';

import {
  Box,
  useColorModeValue,
  Tooltip,
  Icon,
  useToast,
} from '@chakra-ui/react';

import { FaFire } from 'react-icons/fa';

import { apiUpdateLead } from 'api';

import type { APILeadsListResultData } from 'types/api.types';

interface LeadHotProps {
  lead: APILeadsListResultData;
  onSuccess?: (data: APILeadsListResultData) => void;
}

const LeadHot = ({ lead, onSuccess }: LeadHotProps) => {
  const [isHot, setIsHot] = useState(lead.is_hot);
  const hotLeadColor = useColorModeValue('red.500', 'red.300');
  const toast = useToast();

  const { mutate, isPending } = useMutation({
    mutationFn: (isHot: boolean) => apiUpdateLead(lead.id, { is_hot: isHot }),
    onSuccess: (data) => {
      toast({
        title: 'Lead hot status updated',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });

      setIsHot(data.is_hot);

      if (onSuccess) onSuccess(data);
    },
  });

  useEffect(() => {
    setIsHot(lead.is_hot);
  }, [lead.is_hot]);

  const handleToggleHot = (e: React.MouseEvent) => {
    e.stopPropagation();
    mutate(!isHot);
  };

  return (
    <Tooltip
      label={isHot ? 'Remove hot status' : 'Mark as hot'}
      placement="top"
    >
      <Box
        cursor="pointer"
        onClick={handleToggleHot}
        opacity={isPending ? 0.5 : 1}
        transition="all 0.2s"
        display="inline-flex"
      >
        <Icon
          as={FaFire}
          color={isHot ? hotLeadColor : 'gray.400'}
          boxSize={5}
          aria-label={isHot ? 'Hot lead' : 'Mark as hot'}
          _hover={{ color: hotLeadColor }}
          pointerEvents={isPending ? 'none' : 'auto'}
        />
      </Box>
    </Tooltip>
  );
};

export default LeadHot;
